import { observer } from 'mobx-react-lite'
import useStore from 'reusable/mobx-store-utils'
import PostgresMigrationsStore from './PostgresMigrationsStore'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Collapse from '@mui/material/Collapse'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import RefreshIcon from '@mui/icons-material/Refresh'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import type { PostgresMigrationDto } from '@gap/backend/app-services/postgres-migrations/models/PostgresMigrationDto'
import GapAlert from 'reusable/mui/alert/GapAlert'

const statusColors: Record<PostgresMigrationDto.MigrationStatus, 'success' | 'error' | 'warning'> = {
   applied: 'success',
   failed: 'error',
   pending: 'warning',
}

export default observer(function PostgresMigrations() {
   const postgressMigrationsStore = useStore(() => new PostgresMigrationsStore())

   // Auto-load on mount
   if (!postgressMigrationsStore.statusLoader.hasLoadedAtleastOnce && !postgressMigrationsStore.statusLoader.isLoading) {
      postgressMigrationsStore.loadStatus()
   }

   if (postgressMigrationsStore.statusLoader.isLoading && !postgressMigrationsStore.statusLoader.hasLoadedAtleastOnce) {
      return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
         <CircularProgress />
      </Box>
   }

   const { summary } = postgressMigrationsStore

   return <Stack spacing={3}>
      <Typography variant="h5">PostgreSQL Migrations (Drizzle)</Typography>

      {postgressMigrationsStore.status && <GapAlert
         severity={summary.failed > 0 ? 'error' : summary.pending > 0 ? 'warning' : 'success'}
      >
         {summary.total} total — {summary.applied} applied, {summary.failed} failed, {summary.pending} pending
      </GapAlert>}

      <Stack direction="row" spacing={2}>
         <Button
            variant="contained"
            startIcon={postgressMigrationsStore.runLoader.isLoading ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
            onClick={postgressMigrationsStore.runPending}
            disabled={!postgressMigrationsStore.hasPending || postgressMigrationsStore.runLoader.isLoading}
         >
            {postgressMigrationsStore.runLoader.isLoading ? 'Running...' : 'Run Pending'}
         </Button>
         <Button
            variant="outlined"
            startIcon={postgressMigrationsStore.statusLoader.isLoading ? <CircularProgress size={18} /> : <RefreshIcon />}
            onClick={postgressMigrationsStore.loadStatus}
            disabled={postgressMigrationsStore.statusLoader.isLoading}
         >
            Refresh
         </Button>
      </Stack>

      {postgressMigrationsStore.lastRunResult && <GapAlert
         severity={postgressMigrationsStore.lastRunResult.failed ? 'error' : 'success'}
         onClose={postgressMigrationsStore.clearLastRunResult}
      >
         {postgressMigrationsStore.lastRunResult.failed
            ? `Migration failed: ${postgressMigrationsStore.lastRunResult.failed.tag} — ${postgressMigrationsStore.lastRunResult.applied} applied before failure`
            : `${postgressMigrationsStore.lastRunResult.applied} migration${postgressMigrationsStore.lastRunResult.applied === 1 ? '' : 's'} applied successfully`
         }
      </GapAlert>}

      <Paper elevation={10}>
         <Table>
            <TableHead>
               <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Applied At</TableCell>
               </TableRow>
            </TableHead>
            <TableBody>
               {postgressMigrationsStore.migrations.map(migration => (
                  <MigrationRow
                     key={migration.idx}
                     migration={migration}
                     expanded={postgressMigrationsStore.selectedErrorIdx === migration.idx}
                     onToggle={() => postgressMigrationsStore.toggleError(migration.idx)}
                  />
               ))}
            </TableBody>
         </Table>
      </Paper>
   </Stack>
})

const MigrationRow = observer(function MigrationRow({ migration, expanded, onToggle }: {
   migration: PostgresMigrationDto.MigrationEntry
   expanded: boolean
   onToggle: () => void
}) {
   const clickable = migration.status === 'failed' && migration.error

   return <>
      <TableRow
         hover={!!clickable}
         onClick={clickable ? onToggle : undefined}
         sx={clickable ? { cursor: 'pointer' } : undefined}
      >
         <TableCell>{migration.idx}</TableCell>
         <TableCell>
            <Typography variant="body2" fontFamily="monospace">{migration.tag}</Typography>
         </TableCell>
         <TableCell>
            <Chip label={migration.status} color={statusColors[migration.status]} size="small" />
         </TableCell>
         <TableCell>
            {migration.appliedAt
               ? new Date(migration.appliedAt).toLocaleString()
               : '—'
            }
         </TableCell>
      </TableRow>
      {clickable && <TableRow>
         <TableCell colSpan={4} sx={{ p: 0, borderBottom: expanded ? undefined : 'none' }}>
            <Collapse in={expanded}>
               <Box sx={{ p: 2, bgcolor: 'error.light' }}>
                  <Typography variant="subtitle2" gutterBottom>Error Detail</Typography>
                  <Typography variant="body2" fontFamily="monospace" sx={{ whiteSpace: 'pre-wrap' }}>
                     {migration.error?.message}
                  </Typography>
                  {migration.error?.code && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                     SQL Error Code: {migration.error.code}
                  </Typography>}
                  {migration.error?.detail && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                     Detail: {migration.error.detail}
                  </Typography>}
               </Box>
            </Collapse>
         </TableCell>
      </TableRow>}
   </>
})
